import { BRAIN_DUMP_QUESTION_TEXT, foldAnswer, type BrainDumpAsk } from './brainDump.ts';

export interface BrainDumpQuestionInput { thought: string; ask?: BrainDumpAsk }
export interface PendingBrainDumpQuestion extends BrainDumpQuestionInput {
  id: string;
  createdAt: string;
  /** Frozen before handing the thought to the capture queue. Retries cannot change its words. */
  captureText?: string;
}
interface OwnerQuestions { userId: string; questions: PendingBrainDumpQuestion[] }
export interface PendingBrainDump { id: string; userId: string; transcript: string; recordingId?: string }
export interface BrainDumpQuestionState { owners: OwnerQuestions[]; dumps: PendingBrainDump[]; recordingReceipts: PendingBrainDump[]; error: string | null }
interface Storage { read(): string | null; write(raw: string): void; remove(): void }
const unreadable = 'Pending brain-dump thoughts could not be read. Their stored copy has been preserved. Retry storage before continuing.';
const notKept = 'Pending brain-dump thoughts could not be kept. Their previous stored copy has been preserved. Retry storage before continuing.';
const record = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
const nonempty = (value: unknown): value is string => typeof value === 'string' && value.length > 0;
function input(value: unknown): value is BrainDumpQuestionInput {
  return record(value) && typeof value.thought === 'string' && (value.ask === undefined || (typeof value.ask === 'string' && Object.prototype.hasOwnProperty.call(BRAIN_DUMP_QUESTION_TEXT, value.ask)));
}
function question(value: unknown): value is PendingBrainDumpQuestion {
  return input(value) && record(value) && nonempty(value.id) && typeof value.createdAt === 'string' && Number.isFinite(Date.parse(value.createdAt)) &&
    (value.captureText === undefined || typeof value.captureText === 'string');
}

/** The one persistence protocol for web and native. Adapters supply physical storage and UUIDs. */
export function createBrainDumpQuestions(storage: Storage, newId: () => string) {
  let current: BrainDumpQuestionState | undefined;
  const listeners = new Set<() => void>();
  function publish(next: BrainDumpQuestionState) {
    current = next;
    listeners.forEach(listener => listener());
  }
  function persist(owners: OwnerQuestions[], dumps: PendingBrainDump[], recordingReceipts: PendingBrainDump[] = []) {
    if (owners.length || dumps.length || recordingReceipts.length) storage.write(JSON.stringify({ version: 1, owners, dumps, recordingReceipts }));
    else storage.remove();
  }
  function read(): BrainDumpQuestionState {
    try {
      const raw = storage.read();
      if (raw === null) return { owners: [], dumps: [], recordingReceipts: [], error: null };
      const value: unknown = JSON.parse(raw);
      if (!record(value)) throw new Error(unreadable);
      if (value.version === 1 && Array.isArray(value.owners) && Array.isArray(value.dumps)) {
        const owners = value.owners;
        if (!owners.every(owner => record(owner) && nonempty(owner.userId) && Array.isArray(owner.questions) && owner.questions.every(question))) throw new Error(unreadable);
        const valid = owners as OwnerQuestions[];
        if (new Set(valid.map(o => o.userId)).size !== valid.length) throw new Error(unreadable);
        const ids = valid.flatMap(o => o.questions.map(q => q.id));
        if (new Set(ids).size !== ids.length) throw new Error(unreadable);
        if (!value.dumps.every(d => record(d) && nonempty(d.id) && nonempty(d.userId) && typeof d.transcript === 'string' && (d.recordingId === undefined || d.recordingId === d.id))) throw new Error(unreadable);
        const dumps = value.dumps as PendingBrainDump[];
        if (new Set([...ids, ...dumps.map(d => d.id)]).size !== ids.length + dumps.length) throw new Error(unreadable);
        const receipts = value.recordingReceipts ?? [];
        if (!Array.isArray(receipts) || !receipts.every(d => record(d) && nonempty(d.id) && nonempty(d.userId) && typeof d.transcript === 'string' && d.recordingId === d.id) || new Set([...ids, ...dumps.map(d => d.id), ...receipts.map(d => d.id)]).size !== ids.length + dumps.length + receipts.length) throw new Error(unreadable);
        return { owners: valid, dumps, recordingReceipts: receipts as PendingBrainDump[], error: null };
      }
      // An existing user's legacy thoughts are real data. Commit identities before exposing them.
      if (value.version === undefined && nonempty(value.userId) && Array.isArray(value.questions) && value.questions.every(q => input(q) && q.ask !== undefined)) {
        const owners = [{ userId: value.userId, questions: value.questions.map(q => ({ ...q, id: newId(), createdAt: new Date().toISOString(), ...(q.ask === undefined ? { captureText: q.thought } : {}) })) }];
        persist(owners, []);
        return { owners, dumps: [], recordingReceipts: [], error: null };
      }
      throw new Error(unreadable);
    } catch {
      // A failed read is an explicit blocked state; no bytes or provider details reach UI/logging.
      return { owners: current?.owners ?? [], dumps: current?.dumps ?? [], recordingReceipts: current?.recordingReceipts ?? [], error: unreadable };
    }
  }
  const getSnapshot = (): BrainDumpQuestionState => current ?? (current = read());
  function latest(): OwnerQuestions[] {
    const next = read();
    if (next.error) { publish(next); throw new Error(next.error); }
    current = next;
    return next.owners;
  }
  function commit(owners: OwnerQuestions[], dumps = getSnapshot().dumps, recordingReceipts = getSnapshot().recordingReceipts) {
    try { persist(owners, dumps, recordingReceipts); }
    catch { publish({ ...getSnapshot(), error: notKept }); throw new Error(notKept); }
    publish({ owners, dumps, recordingReceipts, error: null });
  }
  function locate(owners: OwnerQuestions[], userId: string, id: string): PendingBrainDumpQuestion {
    const found = owners.find(o => o.userId === userId)?.questions.find(q => q.id === id);
    if (!found) throw new Error('That pending thought does not belong to this account or was already handled.');
    return found;
  }
  return {
    getSnapshot,
    subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; },
    reload() { publish(read()); },
    resetCache() { current = undefined; },
    block() { publish({ ...getSnapshot(), error: unreadable }); },
    rememberDump(userId: string, transcript: string, recordingId?: string): PendingBrainDump {
      if (!nonempty(userId) || !nonempty(transcript)) throw new Error('The dump could not be validated.');
      const owners = latest();
      if (recordingId !== undefined && !nonempty(recordingId)) throw new Error('The recording identity could not be validated.');
      const receipts = getSnapshot().recordingReceipts;
      const receipt = recordingId ? [...getSnapshot().dumps, ...receipts].find(r => r.id === recordingId) : undefined;
      if (receipt) {
        if (receipt.userId !== userId || receipt.transcript !== transcript) throw new Error('That recording identity already belongs to different input. Nothing was replaced.');
        return receipt;
      }
      const dump = { id: recordingId ?? newId(), userId, transcript, ...(recordingId ? { recordingId } : {}) };
      commit(owners, [...getSnapshot().dumps, dump]);
      return dump;
    },
    completeDump(userId: string, id: string, thoughts: BrainDumpQuestionInput[]) {
      if (!thoughts.length || !thoughts.every(input)) throw new Error('The dump could not be validated.');
      const owners = latest(); const dumps = getSnapshot().dumps;
      if (!dumps.some(d => d.id === id && d.userId === userId)) throw new Error('That dump does not belong to this account or was already handled.');
      const added = thoughts.map(q => ({ ...q, id: newId(), createdAt: new Date().toISOString(), ...(q.ask === undefined ? { captureText: q.thought } : {}) }));
      const owner = owners.find(o => o.userId === userId);
      const next = owner ? owners.map(o => o === owner ? { ...o, questions: [...o.questions, ...added] } : o) : [...owners, { userId, questions: added }];
      const completed = dumps.find(d => d.id === id)!;
      commit(next, dumps.filter(d => d.id !== id), completed.recordingId ? [...getSnapshot().recordingReceipts, completed] : getSnapshot().recordingReceipts);
    },
    recordingReceipts(userId: string): PendingBrainDump[] {
      latest();
      return getSnapshot().recordingReceipts.filter(r => r.userId === userId);
    },
    /** Call only after physical raw source deletion is confirmed. Pending transcript custody remains authoritative. */
    retireRecordingReceipt(userId: string, id: string) {
      const owners = latest();
      const receipt = getSnapshot().recordingReceipts.find(r => r.id === id);
      if (receipt && receipt.userId !== userId) throw new Error('That recording receipt does not belong to this account.');
      if (!receipt) return;
      commit(owners, getSnapshot().dumps, getSnapshot().recordingReceipts.filter(r => r.id !== id));
    },
    keep(userId: string, questions: BrainDumpQuestionInput[]) {
      if (!nonempty(userId) || !questions.every(input)) throw new Error('The pending thoughts could not be validated.');
      if (!questions.length) return;
      const owners = latest();
      const added = questions.map(q => ({ ...q, id: newId(), createdAt: new Date().toISOString(), ...(q.ask === undefined ? { captureText: q.thought } : {}) }));
      const owner = owners.find(o => o.userId === userId);
      commit(owner ? owners.map(o => o === owner ? { ...o, questions: [...o.questions, ...added] } : o) : [...owners, { userId, questions: added }]);
    },
    prepare(userId: string, id: string, answer: string): PendingBrainDumpQuestion {
      const owners = latest(); const found = locate(owners, userId, id);
      if (found.captureText !== undefined) return found;
      if (!found.ask) throw new Error('That thought does not need a question.');
      const prepared = { ...found, captureText: foldAnswer(found.thought, found.ask, answer) };
      commit(owners.map(o => o.userId === userId ? { ...o, questions: o.questions.map(q => q.id === id ? prepared : q) } : o));
      return prepared;
    },
    drop(userId: string, id: string) {
      const owners = latest(); locate(owners, userId, id);
      commit(owners.map(o => o.userId === userId ? { ...o, questions: o.questions.filter(q => q.id !== id) } : o).filter(o => o.questions.length));
    },
  };
}
